import React, {Component} from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import _ from 'lodash';
import {log} from '@elixir/logger';
import {createContextConsumerHOC} from './contextHelpers';

// Inspired by https://github.com/javivelasco/react-css-themr#context-theming and
// https://reactjs.org/blog/2018/03/29/react-v-16-3.html

export const THEMEPROVIDER_DEFAULT = 'Consumer';

// Default theme that is used if no ThemeProvider is provided
export const defaultTheme = {
    name: THEMEPROVIDER_DEFAULT,
    getStyles: (componentName, defaultStyles) => {
        return defaultStyles;
    },
    getProps: (componentName, componentProps) => {
        return componentProps;
    },
    switchTheme: () => {
        log.warning('You should not use "theme.switchTheme()" outside a <ThemeProvider>');
    }
};

export const ThemeContext = React.createContext(defaultTheme);

/**
 * Custom class name merger that will concatenate classes that appear
 * in both the defaultStyles and themeStyles
 *
 * @param {any} defaultValue
 * @param {any} themeValue
 * @returns {any} concatenated value or allow default merge
 */
function classNameMerger (defaultValue, themeValue) {
    // concatenate classes if appear in theme and default
    if (defaultValue && themeValue) {
        return `${themeValue} ${defaultValue}`;
    }
    // return nothing to do the default merging
}

/**
 * Create a HOC wrapper that injects 'theme' as a prop
 *
 * @param {React.Component} ComponentToWrap - component to wrap
 * @returns {React.Component} wrapped component
 */
export function withTheme (ComponentToWrap) {
    return createContextConsumerHOC({
        component: ComponentToWrap,
        context: ThemeContext,
        displayName: `Themed${ComponentToWrap.displayName || ComponentToWrap.name}`,
        mapContextToProps: (existingProps, context) => { return {theme: context}; }
    });
}

/**
 * Render by invoking a render prop with 'theme'.
 * This is an alternative to using ThemeContext.Consumer directly. Useful for consuming without
 * using a HOC.
 *
 * @param {function} renderProp - render method to be invoked by the ThemeContext
 * @returns {React.Element} returns JSX wrapping the invocation of the 'renderProp'
 */
export function renderWithTheme (renderProp) {
    return (
        <ThemeContext.Consumer>
            {(theme) => renderProp(theme)}
        </ThemeContext.Consumer>
    );
}

/**
 * A ThemeProvider that can be placed in the app component hierarchy. It takes a theme configuration object
 * and injects a 'theme' object into any consumers. The 'theme' object contains a helper method:
 * theme.getStyle(styleName, defaultStyle) that can be used to get a theme style or return the default.
 */
export default class ThemeProvider extends Component {
    static propTypes = {
        initialConfig: PropTypes.shape({
            name: PropTypes.string.isRequired,
            rootStyle: PropTypes.string,
            componentStyles: PropTypes.object
        }),
        className: PropTypes.string,
        children: PropTypes.node
    };
    static propTypes = {
        children: PropTypes.node
    };
    static bodyStylesMounted = false;

    constructor (props) {
        super(props);
        this.state = this.createStateFromConfig(props.initialConfig);

        // Force applyRootToBody unless already applied
        this.applyRootToBody = !ThemeProvider.bodyStylesMounted;

        // Override for testing
        if (__TEST__) {
            this.applyRootToBody = false;
        }

        if (this.applyRootToBody) {
            ThemeProvider.bodyStylesMounted = true;
        }
    }

    /**
     * @param {object} config configuration for the ThemeProvider
     * @returns {object} ThemeProvider state
     */
    createStateFromConfig = (config) => {
        // put the callbacks into state so that the same instance will be used
        // for each render until the next change
        const theme = {
            name: config ? config.name : THEMEPROVIDER_DEFAULT,
            getStyles: this.getStyles,
            getProps: this.getProps,
            switchTheme: this.switchTheme
        };
        return {config, theme};
    };

    /**
     * Set the theme for this theme provider by providing another theme configuration.
     * This is to change the theme after the initial load at runtime
     *
     * @param {Object} newConfig - new theme configuration object
     */
    switchTheme = (newConfig) => {
        if (this.state.config !== newConfig) {
            this.setState(this.createStateFromConfig(newConfig));
            if (this.applyRootToBody) {
                this.applyRootStyleToBody((newConfig) ? newConfig.rootStyle : undefined);
            }
        }
    };

    /**
     * Apply root style to 'body' element
     * @param {string} rootStyle - root class to apply
     */
    applyRootStyleToBody (rootStyle) {
        document.body.className = cx(
            this._bodyClasses,
            'themeRoot',
            this.props.className,
            rootStyle
        );
    }

    /**
     * Get a theme styles or defaults as appropriate
     *
     * @param {string} componentName - name of the component to find (eg. 'Button')
     * @param {object} defaultStyles - default styles object (eg styles imported from *-hashed.css)
     * @returns {object} theme or default styles
     */
    getStyles = (componentName, defaultStyles) => {
        const {config} = this.state;
        if (config) {
            const componentConfig = _.get(config, `componentStyles.${componentName}`);
            if (componentConfig) {
                const {
                    styles: themeStyles = componentConfig,
                    replace = false
                } = componentConfig;

                // if no default, just return themeStyles
                if (!defaultStyles) {
                    return themeStyles;
                }

                // OPTION: if performance is a problem, consider caching
                if (!replace) {
                    // custom merge defaultStyles and themeStyles
                    return _.mergeWith(
                        {},
                        defaultStyles,
                        themeStyles,
                        classNameMerger
                    );
                }
                return Object.assign({}, defaultStyles, themeStyles);
            }
        }
        return defaultStyles;
    };

    /**
     * Get the root style from a theme. This should be a single string.
     *
     * @returns {string} root style from theme or default style
     */
    getRootStyle = () => {
        const {config} = this.state;
        return config ? config.rootStyle : undefined;
    };

    /**
     * Get a theme props as appropriate
     *
     * @param {string} componentName - name of the component to find (eg. 'Button')
     * @param {object} componentProps - component props to be merged with theme props
     * @returns {object} component props + theme props
     */
    getProps = (componentName, componentProps) => {
        const {config} = this.state;
        if (config) {
            const componentConfig = _.get(config, `componentStyles.${componentName}`);
            if (componentConfig && componentConfig.componentProps) {
                return {...componentProps, ...componentConfig.componentProps};
            }
        }
        return componentProps;
    };

    componentDidMount () {
        if (this.applyRootToBody) {
            if (!this._bodyClasses) {
                this._bodyClasses = document.body.className;
            }
            this.applyRootStyleToBody(this.getRootStyle());
        }
    }

    componentWillUnmount () {
        if (this.applyRootToBody) {
            document.body.className = this._bodyClasses;
            this._bodyClasses = undefined;
            ThemeProvider.bodyStylesMounted = false;
        }
    }

    render () {
        const {children, className} = this.props;
        const rootClasses = (!this.applyRootToBody) ? cx(
            'themeRoot',
            className,
            this.getRootStyle()
        ) : null;

        return (
            <ThemeContext.Provider value={this.state.theme}>
                <div className={rootClasses}>
                    {children}
                </div>
            </ThemeContext.Provider>
        );
    }
}
